import { useTranslation } from 'react-i18next';

import { QDUELS_PLACEMENT_MATCHES, QDUELS_TIERS, type QduelsRating } from '@/types/qduels';
import { cn } from '@/lib/cn';

/**
 * The ranked circle: ONE ring around a Play button, and which ring it is
 * depends on where the player is.
 *
 * While placing → the ring is a ten-slot checklist, one slot per rated match.
 * Once ranked → the same ring becomes progress through the current tier.
 *
 * A ring needs a current AND a total, and `ratedCount/10` is the only signal
 * qduels gives us that is monotonic with a hard ceiling. XP has a band to fill
 * but it also goes DOWN after a loss, so it earns the ring only once placement
 * has settled it. Streaks and win counts don't exist server-side at all —
 * drawing them would be fiction.
 * See docs/analysis/2026-07-17-ranked-circle-design.md (which asked for both
 * rings at once; on screen they read as one smeared double-arc, so this shows
 * one at a time — same radius, so the swap at ten matches is a graduation
 * rather than a reflow).
 *
 * Plain SVG, no library: arcs via stroke-dasharray/dashoffset.
 */

const SIZE = 168;
const RING_R = 76; // one ring, stroke 10 → outer edge 81 inside the 84 half-box
const TICK_GAP_DEG = 5;

const TAU = 2 * Math.PI;

/** Where the player sits inside their tier band: 0..1, or null when unknowable. */
function tierProgress(xp: number | null, tierIndex: number | null): number | null {
  if (xp == null || tierIndex == null) return null;
  const band = QDUELS_TIERS[tierIndex];
  const next = QDUELS_TIERS[tierIndex + 1];
  // Master has no next band — no denominator, so no fraction. The caller draws
  // it full instead of inventing a 100%.
  if (!band || !next) return null;
  const span = next.min - band.min;
  if (span <= 0) return null;
  return Math.min(1, Math.max(0, (xp - band.min) / span));
}


export function RankedCircle({
  rating,
  loading,
  onPlay,
  className,
}: {
  rating: QduelsRating | undefined;
  loading?: boolean;
  onPlay: () => void;
  className?: string;
}) {
  const { t } = useTranslation('duels');

  // A rating we couldn't read is NOT a rating of zero. With the flag off, a 5xx
  // or a still-loading query we draw the button alone: an empty ring would read
  // as "you have no points", and XP starts at 1000 — the opposite of true.
  const known = !loading && rating?.enabled === true;
  const xp = known ? (rating?.xp ?? null) : null;
  const tierIndex = known ? (rating?.tier?.index ?? null) : null;
  const tierName = known ? (rating?.tier?.name ?? null) : null;
  const ratedCount = known ? (rating?.ratedCount ?? 0) : 0;

  const unranked = known && xp == null;
  const placing = known && xp != null && ratedCount < QDUELS_PLACEMENT_MATCHES;
  const isMaster = tierIndex != null && tierIndex === QDUELS_TIERS.length - 1;
  const showTicks = known && ratedCount < QDUELS_PLACEMENT_MATCHES;

  const progress = tierProgress(xp, tierIndex);

  const ringC = TAU * RING_R;
  // Master fills the ring outright: there is no next threshold to measure against,
  // so a fraction would be made up.
  const outerFill = isMaster ? 1 : (progress ?? 0);

  return (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          aria-hidden
          className="absolute inset-0"
        >
          {/* −90° so the ring starts at twelve o'clock, where a player looks. */}
          <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
            {/* ONE ring at a time, never two.
                The design called for the tier ring and the placement ticks
                together, the tier one dimmed. Rendered, they were both brand
                cyan 8px apart and read as a single smeared double-arc — and the
                dimming was trying to say "ignore this" about something we had
                just drawn. So: while you're placing, the ring IS the checklist;
                once you're ranked it becomes the tier. Same radius, so the swap
                at ten matches lands as a graduation, not a reflow. */}

            {/* Track — only when we actually have a rating. With the flag off,
                an error, or mid-load, an empty grey ring would read as "you have
                zero points": the button stands alone instead. */}
            {known && !showTicks && (
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RING_R}
                fill="none"
                strokeWidth={10}
                className="stroke-bg-tertiary"
              />
            )}

            {/* Tier progress — ranked only. */}
            {known && !showTicks && (
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RING_R}
                fill="none"
                strokeWidth={10}
                strokeLinecap="round"
                className="stroke-brand transition-[stroke-dashoffset] duration-700"
                strokeDasharray={ringC}
                strokeDashoffset={ringC * (1 - outerFill)}
              />
            )}

            {/* The placement checklist: ten slots, one per rated match, gone for
                good at ten. Empty slots are muted-grey, not bg-tertiary — at
                #282828 on black they were invisible, and a checklist whose empty
                boxes can't be seen isn't a checklist. */}
            {showTicks &&
              Array.from({ length: QDUELS_PLACEMENT_MATCHES }, (_, i) => {
                const seg = 360 / QDUELS_PLACEMENT_MATCHES;
                const arc = ((seg - TICK_GAP_DEG) / 360) * TAU * RING_R;
                const done = i < ratedCount;
                return (
                  <circle
                    key={i}
                    cx={SIZE / 2}
                    cy={SIZE / 2}
                    r={RING_R}
                    fill="none"
                    strokeWidth={10}
                    strokeLinecap="round"
                    className={cn(
                      'transition-[stroke,opacity] duration-500',
                      done ? 'stroke-brand' : 'stroke-fg-muted',
                    )}
                    opacity={done ? 1 : 0.3}
                    strokeDasharray={`${arc} ${TAU * RING_R - arc}`}
                    strokeDashoffset={-((i * seg) / 360) * TAU * RING_R}
                  />
                );
              })}
          </g>
        </svg>

        {/* Only the centre is tappable: two concentric targets on 168px is a
            guaranteed mis-tap. The button is NEVER hidden — not while loading,
            not on error, not for a brand-new player. */}
        <button
          type="button"
          onClick={onPlay}
          aria-label={t('queue.find', { defaultValue: 'Find match' })}
          className={cn(
            'absolute left-1/2 top-1/2 flex h-[104px] w-[104px] -translate-x-1/2 -translate-y-1/2',
            'flex-col items-center justify-center rounded-full bg-brand px-3 text-center',
            'text-sm font-bold uppercase leading-tight tracking-wide text-brand-fg',
            'transition-transform active:scale-95',
          )}
        >
          {unranked
            ? t('ranked.play_first', { defaultValue: 'Play your first' })
            : t('queue.find', { defaultValue: 'Find match' })}
        </button>

        {/* The rings are decorative SVG; this is what a screen reader gets.
            Pattern lifted from <RatingBar />, not invented. */}
        {known && !unranked && progress != null && (
          <span
            className="sr-only"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            aria-label={t('ranked.tier_progress_aria', {
              defaultValue: 'Progress in {{tier}}',
              tier: tierName ?? '',
            })}
          />
        )}
      </div>

      {/* Sotto il cerchio resta SOLO ciò che spiega l'anello.
          Il punteggio XP e il "quanto manca al prossimo tier" sono stati tolti:
          il tier ora vive sopra, grande, sotto il titolo 1v1 — e un numero Elo
          accanto a un anello che già mostra lo stesso progresso era la stessa
          informazione detta due volte. Restano gli stati che senza una frase
          non si capirebbero. */}
      <div className="flex min-h-[1.25rem] flex-col items-center text-center">
        {placing && (
          <p className="text-[11px] text-fg-muted">
            {t('ranked.placement', {
              defaultValue: 'Placement {{done}}/{{total}} — ranked at {{total}}',
              done: ratedCount,
              total: QDUELS_PLACEMENT_MATCHES,
            })}
          </p>
        )}
        {unranked && (
          <p className="text-[11px] text-fg-muted">
            {t('ranked.unranked_hint', {
              defaultValue: '{{total}} matches to get ranked',
              total: QDUELS_PLACEMENT_MATCHES,
            })}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * The player's rank, stated plainly under the section title.
 *
 * Was a small chip beside "1v1"; it's the one piece of status that matters, so
 * it gets the size. Muted while the rank isn't settled — during placement the
 * K-factor is 48 and the tier can move every single match, so showing it in
 * brand would promise something that isn't stable yet.
 */
export function RankedStatus({
  rating,
  level,
}: {
  rating: QduelsRating | undefined;
  /** QBank wallet level for the SAME coin (the qduels XP is the QRAT balance). */
  level?: number | null;
}) {
  const { t } = useTranslation('duels');
  if (!rating?.enabled) return null;

  const placing = rating.xp != null && rating.ratedCount < QDUELS_PLACEMENT_MATCHES;
  const settled = rating.tier != null && !placing;

  // "Non classificato" is a STATE, not a rank: giving it rank-sized type would
  // shout the one thing the player hasn't earned yet.
  if (rating.tier == null) {
    return (
      <span className="text-sm font-bold uppercase tracking-wide text-fg-muted">
        {t('ranked.unranked', { defaultValue: 'Unranked' })}
      </span>
    );
  }

  return (
    <div className="flex flex-col items-center gap-0.5">
      <span
        className={cn(
          'text-lg font-extrabold uppercase tracking-wide',
          settled ? 'text-brand' : 'text-fg-muted',
        )}
      >
        {rating.tier.name}
      </span>
      {/* The number line: Elo + wallet level, one size down from the rank.
          Restored here after CurrencyHud dropped its EXP pill pointing at this
          exact spot — for a moment the level existed nowhere in the app. */}
      {rating.xp != null && (
        <span className="text-xs font-semibold tabular-nums text-fg-muted">
          {rating.xp.toLocaleString()} XP
          {level != null && ` · Lv ${level}`}
        </span>
      )}
    </div>
  );
}
